/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, MouseEvent as ReactMouseEvent} from 'react';
import classnames from 'classnames';
import {
  ClockDialView,
  getClockOptionLabel,
  getClockOptionPosition,
  isClockOptionSelected
} from './ClockDialUtils';

export interface ClockDialOptionsProps {
  classes: Record<string, string>;
  onOptionClick: (value: number) => (event: ReactMouseEvent<HTMLButtonElement>) => void;
  onOptionMouseDown: (event: ReactMouseEvent<HTMLButtonElement>) => void;
  options: number[];
  selectedHour: number;
  selectedMinute: number;
  view: ClockDialView | null;
}

/**
 * Renders the selectable hour or minute labels positioned around the
 * clock face.
 *
 * ```typescript
 *
 * import {ClockDialOptions} from '@ion/dls/src/components/date/ClockDialOptions';
 *
 * <ClockDialOptions classes={classes}
 *   view={ClockDialView.Hours}
 *   options={CLOCK_HOURS}
 *   selectedHour={9}
 *   selectedMinute={30}
 *   onOptionClick={onOptionClick}
 *   onOptionMouseDown={onOptionMouseDown} />
 *
 * ```
 */
export const ClockDialOptions = ({
  classes,
  onOptionClick,
  onOptionMouseDown,
  options,
  selectedHour,
  selectedMinute,
  view
}: ClockDialOptionsProps) => (
  <>
    {options.map((value, index) => {
      const isSelected = isClockOptionSelected({
        hour   : selectedHour,
        minute : selectedMinute,
        value,
        view
      });
      return (
        <button key={value}
          type='button'
          data-clock-option
          className={classnames(classes.option, {
            [classes.activeOption] : isSelected
          })}
          style={getClockOptionPosition(index)}
          onMouseDown={onOptionMouseDown}
          onClick={onOptionClick(value)}>
          {getClockOptionLabel(view, value)}
        </button>
      );
    })}
  </>
);

export default memo(ClockDialOptions);
